/**
 * Date Input Component with IMask
 */
/* eslint-disable */
import IMask from 'imask';

class DateInput {
  /**
   * @param {HTMLInputElement} input - Input element
   * @param {Object} options - Options
   * @param {Object} options.messages - Messages instance for translations
   */
  constructor(input, options = {}) {
    if (!input) return;

    this.input = input;
    this.container = input.closest('.form__date');
    this.messages = options.messages;

    const lang = document.documentElement.lang;
    this.init(lang);
  }

  init(lang) {
    this.mask = IMask(this.input, {
      mask: lang === 'en-US' ? 'MM / DD / YYYY' : 'MM / DD / AAAA',
      blocks: {
        MM: {
          mask: IMask.MaskedRange,
          from: 1,
          to: 12,
          maxLength: 2,
          placeholderChar: 'M',
        },
        DD: {
          mask: IMask.MaskedRange,
          from: 1,
          to: 31,
          maxLength: 2,
          placeholderChar: 'D',
        },
        AAAA: {
          mask: IMask.MaskedRange,
          from: 1900,
          to: 2100,
          placeholderChar: 'A',
        },
        YYYY: {
          mask: IMask.MaskedRange,
          from: 1900,
          to: 2100,
          placeholderChar: 'Y',
        },
      },
      lazy: false,
      overwrite: true,
    });
    // Expose mask instance for shared form serializers (DOMUtils.getFieldValue)
    // eslint-disable-next-line no-underscore-dangle
    this.input._mask = this.mask;

    this.mask.on('accept', () => this.updateState());
    this.updateState();
  }

  updateState() {
    const isComplete = this.mask.unmaskedValue.length === 8; // MMDDYYYY

    if (this.container) {
      if (isComplete) {
        this.container.classList.add('form__date--filled');
      } else {
        this.container.classList.remove('form__date--filled');
      }
    }

    if (this.input.hasAttribute('required') && !isComplete) {
      const tempInput = document.createElement('input');
      tempInput.required = true;
      this.input.setCustomValidity(tempInput.validationMessage);
    } else {
      this.input.setCustomValidity('');
    }

    // Clear error as user types
    this.clearError();
  }

  showError() {
    if (!this.container) return;

    this.container.classList.add('form__date--error');

    let tip = this.container.querySelector('.form__date-error-tip');
    if (!tip) {
      tip = document.createElement('span');
      tip.className = 'form__date-error-tip wpcf7-not-valid-tip';
      this.container.appendChild(tip);
    }

    const tempInput = document.createElement('input');
    tempInput.required = true;
    tip.textContent = this.messages?.required || tempInput.validationMessage || 'This field is required.';
    tip.hidden = false;
    tip.setAttribute('aria-hidden', 'false');
  }

  clearError() {
    if (!this.container) return;

    this.container.classList.remove('form__date--error');

    const tip = this.container.querySelector('.form__date-error-tip');
    if (tip) {
      tip.hidden = true;
      tip.setAttribute('aria-hidden', 'true');
    }
  }

  /**
   * Validate the date field and show error if invalid
   * @returns {boolean} - true if valid
   */
  validate() {
    const isRequired = this.input.hasAttribute('aria-required') || this.input.hasAttribute('required');
    const isComplete = this.mask.unmaskedValue.length === 8;

    if (isRequired && !isComplete) {
      this.showError();
      return false;
    }

    this.clearError();
    return true;
  }

  /**
   * Reset the field: clear value through IMask so the placeholder is restored
   */
  reset() {
    this.mask.unmaskedValue = '';
    this.mask.updateControl();
    this.clearError();
  }

  /**
   * Initialize all date inputs in a container
   * @param {HTMLElement} container
   * @param {Object} options
   * @returns {DateInput[]}
   */
  static initAll(container, options = {}) {
    const inputs = container.querySelectorAll('.form__date-input');
    return Array.from(inputs).map((input) => new DateInput(input, options));
  }
}

export default DateInput;
